import React, { useRef, useState } from 'react';
import { ApiError, apiFetch } from '../../lib/api';
import type { TournamentDetail } from '../../hooks/useScoringTournament';
import { PinDialog } from '../common/PinDialog';

interface ClearScoresDialogProps {
  tournamentId: string;
  shooterId: string;
  shooterName: string;
  /** Saved shots that will go. */
  shotCount: number;
  onClose: () => void;
  onCleared: (result: { cleared: number; leaderboard: TournamentDetail['leaderboard'] }) => void;
}

/** Asks for the clear-scores PIN. The PIN is checked on the server only. */
export function ClearScoresDialog({
  tournamentId,
  shooterId,
  shooterName,
  shotCount,
  onClose,
  onCleared,
}: ClearScoresDialogProps) {
  const [pin, setPin] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const busyRef = useRef(false);

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    if (!pin.trim() || busyRef.current) return;
    busyRef.current = true;
    setBusy(true);
    setError(null);
    try {
      const body = await apiFetch<{ cleared: number; leaderboard: TournamentDetail['leaderboard'] }>(
        `/api/admin/shooting/tournaments/${tournamentId}/shooters/${shooterId}/clear-scores`,
        { method: 'POST', body: JSON.stringify({ pin: pin.trim() }) },
      );
      onCleared({ cleared: body.cleared, leaderboard: body.leaderboard });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not clear the scores. Try again.');
      setPin('');
    } finally {
      busyRef.current = false;
      setBusy(false);
    }
  }

  const shots = `${shotCount} saved shot${shotCount === 1 ? '' : 's'}`;
  return (
    <PinDialog
      titleId="clear-scores-title"
      title={`Clear ${shooterName}'s scores?`}
      description={`This removes all ${shots} for ${shooterName} and updates the public board. The shooter stays in the list.`}
      pin={pin}
      onPinChange={(next) => {
        setPin(next);
        setError(null);
      }}
      error={error}
      busy={busy}
      confirmLabel="Clear scores"
      busyLabel="Clearing…"
      onSubmit={(event) => void submit(event)}
      onClose={onClose}
    />
  );
}
